import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    getUsername,
    updateSessionCampus,
} from '../../../services/session.js';
import { reloadSessionCampusItems } from '../../../libs.jsx';
import {
    fetchAlertSettings,
    fetchCampusRow,
    fetchHighPriorityEntities,
    fetchEventTypes,
    fetchAFRDevices,
    fetchAllDeviceTypes,
    fetchReasonCodes,
    fetchGlobalReasonCodes,
    fetchPreformNotes,
    saveAlertSettings,
    saveCustomSettings,
    savePriorityEntities,
    saveAFRDevices,
    addReasonCode,
    editReasonCode,
    deleteReasonCodes,
    addPreformNote,
    editPreformNote,
    deletePreformNotes,
    importPreformNotes,
    saveServerSettings,
} from './api/cloudSettingsApi.js';

import CloudAlertSettingsTab from './components/CloudAlertSettingsTab.jsx';
import CloudCustomizationTab from './components/CloudCustomizationTab.jsx';
import HighPriorityTab from './components/HighPriorityTab.jsx';
import CloudDeviceSettingsTab from './components/CloudDeviceSettingsTab.jsx';
import CloudReasonSettingsTab from './components/CloudReasonSettingsTab.jsx';
import PreformNotesTab from './components/PreformNotesTab.jsx';
import CloudServerSettingsTab from './components/CloudServerSettingsTab.jsx';
import CloudReasonDrawer from './components/CloudReasonDrawer.jsx';
import PreformNoteDrawer from './components/PreformNoteDrawer.jsx';

import './system-settings-page.css';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CloudSystemSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    const session = useMemo(() => getSession(), []);

    // Determine if preform notes feature is enabled
    const preformEnabled = useMemo(() => {
        return !!session?.event_notes_active && !!session?.event_notes_preform;
    }, [session]);

    const campusList = useMemo(() => {
        const list = getCampusList() || [];
        // Filter to only cloud campuses
        return list.filter((c) => {
            const cfg = session?.campusData?.[c.campus_id?.toString()];
            return cfg?.is_cloud;
        });
    }, [session]);

    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // -- Tab definitions (conditional) --
    const TABS = useMemo(() => {
        const tabs = [
            { key: 'alert', label: 'Alerts' },
            { key: 'custom', label: 'Customization' },
            { key: 'priority', label: 'High Priority' },
            { key: 'device', label: 'Devices' },
            { key: 'reason', label: 'Reasons' },
        ];
        if (preformEnabled) {
            tabs.push({ key: 'preform', label: 'Note Templates' });
        }
        tabs.push({ key: 'server', label: 'Server' });
        return tabs;
    }, [preformEnabled]);

    // -- Tab state --
    const [activeTab, setActiveTab] = useState('alert');

    // -- Data stores --
    const [alertData, setAlertData] = useState({});
    const [campusRow, setCampusRow] = useState({});
    const [priorityEntities, setPriorityEntities] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [afrDevices, setAFRDevices] = useState([]);
    const [allDeviceTypes, setAllDeviceTypes] = useState([]);
    const [reasonCodes, setReasonCodes] = useState([]);
    const [globalReasons, setGlobalReasons] = useState([]);
    const [preformNotes, setPreformNotes] = useState([]);

    // -- Loading / Saving --
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // -- Drawers / Dialogs --
    const [reasonDrawerOpen, setReasonDrawerOpen] = useState(false);
    const [reasonEditTarget, setReasonEditTarget] = useState(null);
    const [preformDrawerOpen, setPreformDrawerOpen] = useState(false);
    const [preformEditTarget, setPreformEditTarget] = useState(null);
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    // -- Toast --
    const [toast, setToast] = useState(null);
    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        try {
            const promises = [
                fetchAlertSettings(cId),
                fetchCampusRow(cId),
                fetchHighPriorityEntities(cId),
                fetchEventTypes(cId),
                fetchAFRDevices(cId),
                fetchAllDeviceTypes(cId),
                fetchReasonCodes(cId),
                fetchGlobalReasonCodes(cId),
            ];
            if (preformEnabled) {
                promises.push(fetchPreformNotes(cId));
            }

            const results = await Promise.all(promises);
            const [alert, campus, priority, evTypes, afr, devTypes, reasons, globalR] = results;

            // Alert data — convert seconds → minutes
            const alertRow = Array.isArray(alert) && alert.length > 0 ? alert[0] : {};
            setAlertData({
                alert_setting_id: alertRow.alert_setting_id || 0,
                accept_required: alertRow.accept_required === 1,
                finish_required: alertRow.finish_required === 1,
                reason_required: alertRow.reason_required === 1,
                release_timeout: alertRow.release_timeout ? alertRow.release_timeout / 60 : 1,
                finish_timeout: alertRow.finish_timeout ? alertRow.finish_timeout / 60 : 5,
                clear_timeout: alertRow.clear_timeout ? alertRow.clear_timeout / 60 : 4,
                alert_ttl: alertRow.alert_ttl ? alertRow.alert_ttl / 60 : 60,
            });

            // Campus row (customization + server fields)
            const campusRowData = Array.isArray(campus) && campus.length > 0 ? campus[0] : {};
            setCampusRow(campusRowData);

            // Priority entities → extract event_type_ids
            setPriorityEntities((priority || []).map((p) => p.event_type_id));

            // Event types
            setEventTypes(evTypes || []);

            // AFR devices → extract composite IDs
            setAFRDevices((afr || []).map((d) => `${d.device_type_id}-${d.manufacture_id}`));

            // All device types
            setAllDeviceTypes(devTypes || []);

            // Reason codes
            setReasonCodes(reasons || []);

            // Global reasons
            setGlobalReasons(globalR || []);

            // Preform notes (conditional)
            if (preformEnabled && results[8]) {
                setPreformNotes(results[8] || []);
            }
        } catch (err) {
            console.error('[CloudSystemSettingsPage] Load error:', err);
            showToast('Failed to load settings data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [preformEnabled, showToast]);

    // Initial load + reload on campus switch
    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch
    // ======================================================================
    const handleCampusChange = (newId) => {
        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;

        setCampusId(newId);
        updateSessionCampus(newId);

        // If switching between cloud and non-cloud, reload the whole page
        if (oldIsCloud !== newIsCloud) {
            window.location.reload();
        }
    };

    // ======================================================================
    // Save handlers
    // ======================================================================

    // --- Alert Settings ---
    const handleSaveAlert = async (formData) => {
        setSaving(true);
        const payload = [{
            alert_setting_id: formData.alertSettingId,
            campus_id: campusId,
            accept_required: formData.acceptRequired ? 1 : 0,
            finish_required: formData.finishRequired ? 1 : 0,
            reason_required: formData.reasonRequired ? 1 : 0,
            release_timeout: formData.releaseTimeout * 60,
            finish_timeout: formData.finishTimeout * 60,
            clear_timeout: formData.clearTimeout * 60,
            alert_ttl: formData.alertTTL * 60,
            modified_by: getUserId()
        }];
        const oldPayload = [{
            alert_setting_id: alertData.alert_setting_id,
            campus_id: campusId,
            accept_required: alertData.accept_required ? 1 : 0,
            finish_required: alertData.finish_required ? 1 : 0,
            reason_required: alertData.reason_required ? 1 : 0,
            release_timeout: alertData.release_timeout * 60,
            finish_timeout: alertData.finish_timeout * 60,
            clear_timeout: alertData.clear_timeout * 60,
            alert_ttl: alertData.alert_ttl * 60,
        }];

        const [ok] = await saveAlertSettings(payload, oldPayload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            // Update session alert_ttl for real-time use
            reloadSessionCampusItems(campusId, ['alert_ttl'], [formData.alertTTL * 60], false);
            setAlertData({
                alert_setting_id: formData.alertSettingId,
                accept_required: formData.acceptRequired,
                finish_required: formData.finishRequired,
                reason_required: formData.reasonRequired,
                release_timeout: formData.releaseTimeout,
                finish_timeout: formData.finishTimeout,
                clear_timeout: formData.clearTimeout,
                alert_ttl: formData.alertTTL,
            });
            showToast('Alert settings saved.');
        } else {
            showToast('Failed to save alert settings.', 'error');
        }
    };

    // --- Customization ---
    const handleSaveCustom = async ({ isNameEditable, isGroupEditable }) => {
        setSaving(true);
        const payload = [{
            campus_id: campusId,
            is_name_editable: isNameEditable ? 1 : 0,
            is_group_editable: isGroupEditable ? 1 : 0,
            modified_by: getUserId(),
        }];
        const oldPayload = [{
            campus_id: campusId,
            is_name_editable: campusRow.is_name_editable,
            is_group_editable: campusRow.is_group_editable,
        }];

        const [ok] = await saveCustomSettings(payload, oldPayload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setCampusRow((prev) => ({
                ...prev,
                is_name_editable: isNameEditable ? 1 : 0,
                is_group_editable: isGroupEditable ? 1 : 0,
            }));
            showToast('Customization settings saved.');
        } else {
            showToast('Failed to save customization settings.', 'error');
        }
    };

    // --- High Priority ---
    const handleSavePriority = async (selectedIds) => {
        setSaving(true);
        const payload = [{ campus_id: campusId, priority_entities: selectedIds }];
        const oldPayload = [{ campus_id: campusId, priority_entities: priorityEntities }];

        const [ok] = await savePriorityEntities(payload, oldPayload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setPriorityEntities(selectedIds);
            showToast('High priority events saved.');
        } else {
            showToast('Failed to save high priority events.', 'error');
        }
    };

    // --- AFR Devices ---
    const handleSaveDevices = async (selectedIds) => {
        setSaving(true);
        const payload = [{ campus_id: campusId, afr_entities: selectedIds }];
        const oldPayload = [{ campus_id: campusId, afr_entities: afrDevices }];

        const [ok] = await saveAFRDevices(payload, oldPayload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setAFRDevices(selectedIds);
            showToast('Device settings saved.');
        } else {
            showToast('Failed to save device settings.', 'error');
        }
    };

    // --- Reason Codes ---
    const handleAddReason = async ({ description, category }) => {
        setSaving(true);
        const payload = [{
            reason_code_description: description,
            pal_reason_code_id: category,
            campus_id: campusId,
            modified_by: getUserId()
        }];

        const [ok] = await addReasonCode(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setReasonDrawerOpen(false);
            showToast('Reason code added.');
            // Reload reason codes to get server-assigned ID
            const fresh = await fetchReasonCodes(campusId);
            setReasonCodes(fresh || []);
        } else {
            showToast('Failed to add reason code.', 'error');
        }
    };

    const handleEditReason = async ({ id, description, category }) => {
        setSaving(true);
        const target = reasonCodes.find((r) => r.cloud_reason_code_id === id);
        const payload = [{
            cloud_reason_code_id: id,
            campus_id: campusId,
            pal_reason_code_id: category,
            reason_code_description: description,
            modified_by: getUserId(),
        }];
        const oldPayload = [{
            cloud_reason_code_id: id,
            pal_reason_code_id: target?.pal_reason_code_id,
            reason_code_description: target?.reason_code_description,
            campus_id: campusId,
        }];

        const [ok] = await editReasonCode(payload, oldPayload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setReasonDrawerOpen(false);
            setReasonEditTarget(null);
            showToast('Reason code updated.');
            const fresh = await fetchReasonCodes(campusId);
            setReasonCodes(fresh || []);
        } else {
            showToast('Failed to update reason code.', 'error');
        }
    };

    const handleDeleteReasons = async (ids) => {
        if (!ids || ids.length === 0) return;
        setSaving(true);
        const payload = ids.map((id) => ({ cloud_reason_code_id: id }));

        const [ok] = await deleteReasonCodes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setReasonCodes((prev) =>
                prev.filter((r) => !ids.includes(r.cloud_reason_code_id))
            );
            showToast(`${ids.length} reason code${ids.length !== 1 ? 's' : ''} deleted.`);
        } else {
            showToast('Failed to delete reason codes.', 'error');
        }
    };

    // --- Preform Notes ---
    const handleAddPreformNote = async ({ note }) => {
        setSaving(true);
        const payload = [{
            preform_note: note,
            campus_id: campusId,
            modified_by: getUserId(),
        }];

        const [ok] = await addPreformNote(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setPreformDrawerOpen(false);
            showToast('Note template added.');
            const fresh = await fetchPreformNotes(campusId);
            setPreformNotes(fresh || []);
        } else {
            showToast('Failed to add note template.', 'error');
        }
    };

    const handleEditPreformNote = async ({ id, note }) => {
        setSaving(true);
        const target = preformNotes.find((n) => n.preform_note_id === id);
        const payload = [{
            preform_note_id: id,
            preform_note: note,
            campus_id: campusId,
            modified_by: getUserId(),
        }];
        const oldPayload = [{
            preform_note: target?.preform_note,
        }];

        const [ok] = await editPreformNote(payload, oldPayload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setPreformDrawerOpen(false);
            setPreformEditTarget(null);
            showToast('Note template updated.');
            const fresh = await fetchPreformNotes(campusId);
            setPreformNotes(fresh || []);
        } else {
            showToast('Failed to update note template.', 'error');
        }
    };

    const handleDeletePreformNotes = async (ids) => {
        setSaving(true);
        const payload = ids.map((id) => ({
            preform_note_id: id,
            modified_by: getUserId(),
        }));

        const [ok] = await deletePreformNotes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setPreformNotes((prev) => prev.filter((n) => !ids.includes(n.preform_note_id)));
            showToast(`${ids.length} template${ids.length !== 1 ? 's' : ''} deleted.`);
        } else {
            showToast('Failed to delete note templates.', 'error');
        }
    };

    const handleImportPreformNotes = async (fromCampusId) => {
        setSaving(true);
        const payload = [{ from_campus_id: parseInt(fromCampusId), campus_id: campusId }];

        const [ok] = await importPreformNotes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);
        setImportDialogOpen(false);

        if (ok) {
            showToast('Note templates imported successfully.');
            const fresh = await fetchPreformNotes(campusId);
            setPreformNotes(fresh || []);
        } else {
            showToast('Failed to import note templates.', 'error');
        }
    };

    // --- Server Settings ---
    const handleSaveServer = async (formData) => {
        setSaving(true);
        const payload = [{
            emergency_email: formData.emergency_email,
            emergency_text: formData.emergency_text,
            system_offline_time: formData.system_offline_time,
            campus_id: campusId,
            modified_by: getUserId()
        }];
        const oldPayload = [{
            emergency_email: campusRow.emergency_email || '',
            emergency_text: campusRow.emergency_text || '',
            system_offline_time: campusRow.system_offline_time || 15,
            campus_id: campusId,
        }];

        const [ok] = await saveServerSettings(payload, oldPayload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setCampusRow((prev) => ({ ...prev, ...formData }));
            showToast('Server settings saved.');
        } else {
            showToast('Failed to save server settings.', 'error');
        }
    };

    // ======================================================================
    // Campus import options for preform notes
    // ======================================================================
    const importCampusOptions = useMemo(() => {
        if (!session?.acntList || !session?.campusData) return [];
        return session.acntList
            .filter((id) => {
                if (id === campusId) return false;
                const cfg = session.campusData[id.toString()];
                return cfg?.event_notes_active && cfg?.event_notes_preform;
            })
            .map((id) => ({
                id,
                name: session.campusData[id.toString()]?.name || `Campus ${id}`,
            }));
    }, [session, campusId]);

    // ======================================================================
    // TZ offset for preform notes display
    // ======================================================================
    const tzOffset = useMemo(() => {
        return parseInt(session?.campusData?.[campusId?.toString()]?.tz_offset || 0);
    }, [session, campusId]);

    // ======================================================================
    // Render
    // ======================================================================
    const renderTabContent = () => {
        if (loading) {
            return (
                <div className="pp-section">
                    <div className="pp-section__body">
                        <div className="ss-skeleton" style={{ width: '60%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '80%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px' }} />
                    </div>
                </div>
            );
        }

        switch (activeTab) {
            case 'alert':
                return (
                    <CloudAlertSettingsTab
                        alertTTL={alertData.alert_ttl}
                        acceptRequired={alertData.accept_required}
                        finishRequired={alertData.finish_required}
                        reasonRequired={alertData.reason_required}
                        releaseTimeout={alertData.release_timeout}
                        finishTimeout={alertData.finish_timeout}
                        clearTimeout={alertData.clear_timeout}
                        alertSettingId={alertData.alert_setting_id}
                        onSave={handleSaveAlert}
                        saving={saving}
                    />
                );
            case 'custom':
                return (
                    <CloudCustomizationTab
                        isNameEditable={campusRow.is_name_editable === 1}
                        isGroupEditable={campusRow.is_group_editable === 1}
                        onSave={handleSaveCustom}
                        saving={saving}
                    />
                );
            case 'priority':
                return (
                    <HighPriorityTab
                        eventTypes={eventTypes}
                        selectedPriorityIds={priorityEntities}
                        onSave={handleSavePriority}
                        saving={saving}
                    />
                );
            case 'device':
                return (
                    <CloudDeviceSettingsTab
                        allDeviceTypes={allDeviceTypes}
                        selectedDeviceIds={afrDevices}
                        onSave={handleSaveDevices}
                        saving={saving}
                    />
                );
            case 'reason':
                return (
                    <CloudReasonSettingsTab
                        reasonCodes={reasonCodes}
                        globalReasons={globalReasons}
                        onAddReason={() => {
                            setReasonEditTarget(null);
                            setReasonDrawerOpen(true);
                        }}
                        onEditReason={(rc) => {
                            setReasonEditTarget(rc);
                            setReasonDrawerOpen(true);
                        }}
                        onDeleteReasons={handleDeleteReasons}
                        saving={saving}
                    />
                );
            case 'preform':
                return preformEnabled ? (
                    <PreformNotesTab
                        notes={preformNotes}
                        onAddNote={() => {
                            setPreformEditTarget(null);
                            setPreformDrawerOpen(true);
                        }}
                        onEditNote={(note) => {
                            setPreformEditTarget(note);
                            setPreformDrawerOpen(true);
                        }}
                        onDeleteNotes={handleDeletePreformNotes}
                        onImportFromCampus={importCampusOptions.length > 0 ? () => setImportDialogOpen(true) : null}
                        saving={saving}
                        tzOffset={tzOffset}
                    />
                ) : null;
            case 'server':
                return (
                    <CloudServerSettingsTab
                        systemOfflineTime={campusRow.system_offline_time || 15}
                        emergencyEmails={campusRow.emergency_email || ''}
                        emergencyPhones={campusRow.emergency_text || ''}
                        onSave={handleSaveServer}
                        saving={saving}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`}>
                            <i className={`fe ${toast.type === 'success' ? 'fe-check-circle' : 'fe-alert-circle'}`} />
                            {toast.message}
                            <button className="dp-toast__close" onClick={() => setToast(null)}>
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ) : null}

                    {/* Page header */}
                    <div className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <span className="dp-breadcrumb__text">Settings</span>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">System Settings</span>
                                </nav>
                                <div className="dp-campus-row">
                                    <label htmlFor="css-campus-selector" className="dp-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="css-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="dp-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>
                                                    {c.campus_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="dp-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="pp-tab-bar">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Scrollable tab content */}
                    <div className="pp-tab-content">
                        {renderTabContent()}
                    </div>

                    {/* Drawers & Dialogs */}
                    <CloudReasonDrawer
                        open={reasonDrawerOpen}
                        onClose={() => { setReasonDrawerOpen(false); setReasonEditTarget(null); }}
                        onSave={reasonEditTarget ? handleEditReason : handleAddReason}
                        saving={saving}
                        globalReasons={globalReasons}
                        editTarget={reasonEditTarget}
                    />

                    <PreformNoteDrawer
                        open={preformDrawerOpen}
                        onClose={() => { setPreformDrawerOpen(false); setPreformEditTarget(null); }}
                        onSave={preformEditTarget ? handleEditPreformNote : handleAddPreformNote}
                        saving={saving}
                        editTarget={preformEditTarget}
                    />

                    {/* Campus Import dialog for preform notes */}
                    {importDialogOpen ? (
                        <div className="pp-dialog-backdrop" onClick={() => setImportDialogOpen(false)}>
                            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="pp-dialog__header">
                                    <h3 className="pp-dialog__title">Import from Campus</h3>
                                    <button className="dp-drawer__close" onClick={() => setImportDialogOpen(false)}>
                                        <i className="fe fe-x" />
                                    </button>
                                </div>
                                <div className="pp-dialog__body">
                                    <p className="pp-dialog__text" style={{ marginBottom: 'var(--space-3)' }}>
                                        Select a campus to import note templates from. Existing templates will not be affected.
                                    </p>
                                    <select
                                        id="import-campus-select"
                                        className="dp-drawer__select"
                                        defaultValue={importCampusOptions[0]?.id || ''}
                                    >
                                        {importCampusOptions.map((opt) => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="pp-dialog__footer">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setImportDialogOpen(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary"
                                        disabled={saving}
                                        onClick={() => {
                                            const sel = document.getElementById('import-campus-select');
                                            if (sel?.value) handleImportPreformNotes(sel.value);
                                        }}
                                    >
                                        {saving ? 'Importing...' : 'Import'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default CloudSystemSettingsPage;
